import React, { useState } from 'react';
import { X, Calendar, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { parseISO } from 'date-fns';
import { LeavePersonnel } from '../../lib/leavePersonnel';

interface AddLeaveModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    leaveToEdit?: any;
    personnel: LeavePersonnel[];
    selectedPersonnelId?: string | null;
}

const AddLeaveModal: React.FC<AddLeaveModalProps> = ({ isOpen, onClose, onSuccess, leaveToEdit, personnel, selectedPersonnelId }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [personnelId, setPersonnelId] = useState('');
    const users = [...personnel];
    if (leaveToEdit?.personnel_id && !users.some(p => p.id === leaveToEdit.personnel_id)) {
        users.push({ id: leaveToEdit.personnel_id, name: leaveToEdit.personnel_name || 'Eski personel', user_id: leaveToEdit.user_id, branch_id: null });
    }
    const [isManualEntry, setIsManualEntry] = useState(false);
    const [formData, setFormData] = useState({
        user_id: '',
        personnel_name: '',
        leave_type: 'annual',
        start_date: '',
        end_date: '',
        reason: ''
    });

    React.useEffect(() => {
        if (isOpen) {
            setIsManualEntry(false);
            setPersonnelId(leaveToEdit?.personnel_id || selectedPersonnelId || '');

            if (leaveToEdit) {
                setFormData({
                    user_id: leaveToEdit.user_id || '',
                    personnel_name: leaveToEdit.personnel_name || '',
                    leave_type: leaveToEdit.leave_type,
                    start_date: leaveToEdit.start_date,
                    end_date: leaveToEdit.end_date,
                    reason: leaveToEdit.reason || ''
                });
                setIsManualEntry(!leaveToEdit.personnel_id && !!leaveToEdit.personnel_name);
            } else if (user) {
                const selected = personnel.find(p => p.id === selectedPersonnelId);
                setFormData(prev => ({
                    ...prev,
                    user_id: selected?.user_id || '',
                    personnel_name: selected?.name || '',
                    leave_type: 'annual',
                    start_date: '',
                    end_date: '',
                    reason: ''
                }));
            }
        }
    }, [isOpen, user, leaveToEdit, selectedPersonnelId]);

    if (!isOpen) return null;

    const calculateDays = () => {
        if (!formData.start_date || !formData.end_date) return 0;
        const start = parseISO(formData.start_date);
        const end = parseISO(formData.end_date);
        if (end < start) return 0;
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return diffDays;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const isAdmin = user?.role === 'admin' || user?.unitType === 'accounting';
            let finalUserId: string | null | undefined = isAdmin ? formData.user_id : user?.id;
            let finalPersonnelName = formData.personnel_name.trim();
            let finalPersonnelId: string | null = null;
            if (isAdmin && !isManualEntry) {
                const selected = users.find(p => p.id === personnelId);
                if (!selected) {
                    alert('Lütfen bir personel seçiniz.');
                    return;
                }
                finalPersonnelId = selected.id;
                finalUserId = selected.user_id;
                finalPersonnelName = selected.name;
            } else if (!isAdmin) {
                const ownPersonnel = personnel.find(p => p.user_id === user?.id);
                finalPersonnelId = ownPersonnel?.id || null;
                finalPersonnelName = ownPersonnel?.name || user?.name || '';
            }

            if (isAdmin && isManualEntry) {
                finalUserId = null;
                if (!finalPersonnelName) {
                    alert('Lütfen personel adını giriniz.');
                    setLoading(false);
                    return;
                }
            } else if (!finalUserId && !finalPersonnelId) {
                alert('Lütfen bir personel seçiniz veya manuel isim giriniz.');
                setLoading(false);
                return;
            }

            const daysCount = calculateDays();
            if (daysCount <= 0) {
                alert('Bitiş tarihi başlangıç tarihinden önce olamaz.');
                setLoading(false);
                return;
            }

            const leaveData = {
                user_id: finalUserId || null,
                personnel_id: finalPersonnelId,
                personnel_name: finalPersonnelName,
                leave_type: formData.leave_type,
                start_date: formData.start_date,
                end_date: formData.end_date,
                days_count: daysCount,
                reason: formData.reason,
                status: leaveToEdit ? leaveToEdit.status : (isAdmin ? 'approved' : 'pending'),
                approved_by: isAdmin ? user?.id : (leaveToEdit ? leaveToEdit.approved_by : null),
                approved_at: isAdmin ? new Date().toISOString() : (leaveToEdit ? leaveToEdit.approved_at : null)
            };

            let error;
            if (leaveToEdit) {
                const { error: updateError } = await supabase
                    .from('leaves')
                    .update(leaveData)
                    .eq('id', leaveToEdit.id);
                error = updateError;
            } else {
                const { error: insertError } = await supabase
                    .from('leaves')
                    .insert(leaveData);
                error = insertError;
            }

            if (error) throw error;

            onSuccess();
            onClose();
            setFormData({
                user_id: '',
                personnel_name: '',
                leave_type: 'annual',
                start_date: '',
                end_date: '',
                reason: ''
            });
            setIsManualEntry(false);
        } catch (err) {
            console.error('Error saving leave record:', err);
            alert('İşlem sırasında bir hata oluştu: ' + (err as any).message);
        } finally {
            setLoading(false);
        }
    };

    const isAdminOrAccounting = user?.role === 'admin' || user?.unitType === 'accounting';

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
                <div className="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div className="absolute top-0 right-0 pt-4 pr-4">
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none">
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="sm:flex sm:items-start">
                        <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-blue-100 dark:bg-blue-900/30 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                            <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                                {leaveToEdit ? 'İzin Düzenle' : (isAdminOrAccounting ? 'Personel İzin Kaydı Gir' : 'Yeni İzin Talebi')}
                            </h3>

                            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                                {isAdminOrAccounting && (
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Personel Seçimi
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setIsManualEntry(!isManualEntry);
                                                    setPersonnelId('');
                                                    setFormData(prev => ({ ...prev, user_id: '', personnel_name: '' }));
                                                }}
                                                className="text-xs text-blue-600 hover:text-blue-500 underline focus:outline-none"
                                            >
                                                {isManualEntry ? 'Listeden Seç' : 'Manuel İsim Gir'}
                                            </button>
                                        </div>

                                        {isManualEntry ? (
                                            <input
                                                type="text"
                                                required
                                                placeholder="Personel Adı Soyadı Giriniz"
                                                value={formData.personnel_name}
                                                onChange={(e) => setFormData({ ...formData, personnel_name: e.target.value })}
                                                className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white rounded-md p-2"
                                            />
                                        ) : (
                                            <select
                                                required={!isManualEntry}
                                                value={personnelId}
                                                onChange={(e) => {
                                                    const selectedUser = users.find(u => u.id === e.target.value);
                                                    setPersonnelId(e.target.value);
                                                    setFormData({
                                                        ...formData,
                                                        user_id: selectedUser?.user_id || '',
                                                        personnel_name: selectedUser ? selectedUser.name : ''
                                                    });
                                                }}
                                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                            >
                                                <option value="">Seçiniz</option>
                                                {users.length > 0 ? (
                                                    users.map(u => (
                                                        <option key={u.id} value={u.id}>
                                                            {u.name}
                                                        </option>
                                                    ))
                                                ) : (
                                                    <option value="" disabled>Personel bulunamadı (Yetki kontrolü yapınız)</option>
                                                )}
                                            </select>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        İzin Türü
                                    </label>
                                    <select
                                        value={formData.leave_type}
                                        onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                    >
                                        <option value="annual">Yıllık İzin</option>
                                        <option value="sick">Hastalık İzni</option>
                                        <option value="casual">Mazeret İzni</option>
                                        <option value="maternity">Doğum İzni</option>
                                        <option value="paternity">Babalık İzni</option>
                                        <option value="unpaid">Ücretsiz İzin</option>
                                        <option value="other">Diğer</option>
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Başlangıç Tarihi
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.start_date}
                                            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white rounded-md p-2"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Bitiş Tarihi
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.end_date}
                                            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white rounded-md p-2"
                                        />
                                    </div>
                                </div>

                                {formData.start_date && formData.end_date && (
                                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" />
                                        Toplam Süre: {calculateDays()} gün
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Açıklama / Neden
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={formData.reason}
                                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                        className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white rounded-md p-2"
                                        placeholder="İzin nedeninizi kısaca açıklayınız..."
                                    />
                                </div>

                                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                                    >
                                        {loading ? 'Kaydediliyor...' : (leaveToEdit ? 'Güncelle' : 'Talep Oluştur')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                                    >
                                        İptal
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddLeaveModal;
